import { add, format, getDate, getDaysInMonth, isSameWeek, startOfWeek } from 'date-fns';
import { get } from 'lodash';

import {
  DATE_MONTH_FORMAT,
  DAY_LABEL_FORMAT,
  ISO_DATE_FORMAT,
  ISO_WEEK_FORMAT,
  MONTH_LABEL_FORMAT,
  SHORT_MONTH_LABEL_FORMAT,
} from '@/lib/constants';
import { ScheduleChartRange, ScheduleFrequencyType } from '@/types';
import { ScheduleFrequencyApiInterface } from '@/types/api-types';
import { formatToLocationDateTime } from '@/utils/format';
import { formatDateForUser, formatIsoDate } from '@/utils/format/date-time';

export interface ConfigObjInterface {
  readonly type: string;
  readonly chartLabel: string;
  readonly monthLabelFormat: string;
  readonly dayDateLabelFormat: string;
  readonly tooltipFreqLabel: string;
  readonly showDayDateLabel: boolean;
}

export const config: { day: ConfigObjInterface; week: ConfigObjInterface } = {
  day: {
    type: 'day',
    chartLabel: 'Departures per day',
    monthLabelFormat: 'MMMM',
    dayDateLabelFormat: 'd',
    tooltipFreqLabel: 'per day',
    showDayDateLabel: true,
  },
  week: {
    type: 'week',
    chartLabel: 'Departures per week',
    monthLabelFormat: 'MMM',
    dayDateLabelFormat: 'd',
    tooltipFreqLabel: 'per week',
    showDayDateLabel: false,
  },
};

export const getScheduleConfig = (type: ScheduleFrequencyType): ConfigObjInterface => get(config, type);

// builds a date range array to display 1 month of dates
export const createScheduleDateRange = (
  type: ScheduleFrequencyType,
  timezone: string,
  currentDate: Date = new Date(),
): ScheduleChartRange[] => {
  switch (type) {
    case 'week':
      return createScheduleWeekRange(currentDate);
    case 'day':
      return createScheduleDayRange(timezone, currentDate);
    default:
      return [];
  }
};

const createScheduleDayRange = (timezone: string, currentDate: Date): ScheduleChartRange[] => {
  const chartLen = 21; // Day length of chart
  // Move start date back 2 weeks (so we have 1 week back and two forward)
  const startDate = new Date(currentDate).setDate(currentDate.getDate() - chartLen / 3);

  // Get currentDate in location/local time
  const nowInLocationTime = formatToLocationDateTime(currentDate, timezone, ISO_DATE_FORMAT);

  let prevMonth = '';
  let isInFuture = false;

  // Loop to chartLen and add a day to the array for each
  return [...Array(chartLen)].map((_item, i) => {
    const newDate = add(startDate, { days: i });
    const thisMonth = format(newDate, 'MM');
    const isStartOfMonth = thisMonth !== prevMonth && isNotNearEndOfMonth(newDate);
    const isToday = nowInLocationTime === formatIsoDate(newDate);
    // Set isInfuture flag
    isInFuture = isInFuture || isToday;

    prevMonth = thisMonth;

    return {
      key: formatIsoDate(newDate),
      date: formatIsoDate(newDate),
      dateLabel: formatDateForUser(newDate),
      monthLabel: format(newDate, MONTH_LABEL_FORMAT),
      dayLabel: format(newDate, DAY_LABEL_FORMAT),
      isStartOfMonth,
      isToday,
      isInFuture,
    };
  });
};

const createScheduleWeekRange = (currentDate: Date): ScheduleChartRange[] => {
  const chartLen = 20; // Week length of chart
  // Uses start of week instead of currentDate as startDate, (set to Monday for ISO Week)
  const weekStartDate = startOfWeek(currentDate, { weekStartsOn: 1 });
  // Move start date back 12 weeks (or half of chart size)
  weekStartDate.setDate(weekStartDate.getDate() - (chartLen * 7) / 2);

  let prevMonth = '';
  let isInFuture = false;

  // Loop to chartLen and add a week to the array for each
  return [...Array(chartLen)].map((_item, i) => {
    const startOfThisWeek = add(weekStartDate, { weeks: i });
    const endOfThisWeek = add(startOfThisWeek, { days: 6 });
    const thisMonth = format(startOfThisWeek, 'MM');
    const isStartOfMonth = thisMonth !== prevMonth;
    const isToday = isSameWeek(currentDate, startOfThisWeek, { weekStartsOn: 1 });
    // Set isInfuture flag
    isInFuture = isInFuture || isToday;

    prevMonth = thisMonth;

    return {
      key: format(startOfThisWeek, ISO_WEEK_FORMAT),
      date: formatIsoDate(startOfThisWeek),
      dateLabel: `${format(startOfThisWeek, DATE_MONTH_FORMAT)} - ${formatDateForUser(endOfThisWeek)}`,
      monthLabel: format(startOfThisWeek, SHORT_MONTH_LABEL_FORMAT),
      dayLabel: format(startOfThisWeek, DAY_LABEL_FORMAT),
      isStartOfMonth,
      isToday,
      isInFuture,
    };
  });
};

// Checks if current date is close to the end of the month
// To avoid Month label collisions in the UI
export const isNotNearEndOfMonth = (date: Date): boolean => {
  const delta = 4; // 4 days buffer to fit label in
  const daysInMonth = getDaysInMonth(date);
  const currentDay = getDate(date);
  return daysInMonth - currentDay >= delta;
};

export const calcPercentOfMax = (value: number, max: number): number => {
  const num = Math.ceil((value / max) * 100);
  return Number.isFinite(num) ? num : 0;
};

// Trims the frequency schedule data to overlay the date range being used
export const trimFrequencyToRange = (
  frequency: ScheduleFrequencyApiInterface,
  dateRange: ScheduleChartRange[],
): ScheduleFrequencyApiInterface | null => {
  if (!frequency) return null;
  // Converts to array of values
  const asArray = Object.entries(frequency);
  // Filters against dateRange object key
  const filtered = asArray.filter(([key]) => dateRange.some((r) => r.key === key));
  // Converts back to object and returns
  return Object.fromEntries(filtered);
};
